import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useApiCacheRefresh } from '../lib/apiCache';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { KPISidebar } from '../components/KPISidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { DollarSign, TrendingUp, FolderKanban, Plus, ChevronRight, Activity, MapPin, FilePlus, Users as UsersIcon, CheckSquare, Package, Truck, CircleCheckBig } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { HelpTooltip } from '../components/HelpTooltip';
import { StaleProjectsBanner } from '../components/StaleProjectsBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TIME_FILTERS = [
  { value: 'month', label: 'W tym miesiącu' },
  { value: 'quarter', label: 'W tym kwartale' },
  { value: 'year', label: 'W tym roku' },
];

export const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [dashStats, setDashStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [pipeline, setPipeline] = useState(null);
  const [timeFilter, setTimeFilter] = useState('month');
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.role !== 'montaz' && user?.role !== 'guest') {
      fetchDashStats(timeFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFilter]);

  useEffect(() => {
    filterProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, statusFilter]);

  useApiCacheRefresh(() => fetchData());

  const fetchDashStats = useCallback(async (tf) => {
    try {
      const res = await axios.get(`${API_URL}/reports/dashboard-stats?time_filter=${tf}`);
      setDashStats(res.data);
    } catch (err) { console.error("Fallback:", err); }
  }, []);

  const fetchData = async () => {
    try {
      if (user?.role === 'montaz') {
        const projectsRes = await axios.get(`${API_URL}/projects`);
        setProjects(projectsRes.data);
        setStats({
          total_projects: projectsRes.data.length,
          total_offer: 0,
          total_margin: 0,
          avg_margin_percent: 0,
          projects_by_status: {}
        });
      } else {
        const fetches = [
          axios.get(`${API_URL}/reports/stats`),
          axios.get(`${API_URL}/projects`),
        ];
        if (user?.role !== 'guest') {
          fetches.push(axios.get(`${API_URL}/reports/pipeline`));
          fetches.push(axios.get(`${API_URL}/reports/dashboard-stats?time_filter=${timeFilter}`));
        }
        const results = await Promise.all(fetches);
        setStats(results[0].data);
        setProjects(results[1].data);
        if (results[2]) setPipeline(results[2].data);
        if (results[3]) setDashStats(results[3].data);
      }
    } catch (error) {
      toast.error('Błąd pobierania danych');
    } finally {
      setLoading(false);
    }
  };

  const filterProjects = () => {
    if (statusFilter === 'all') {
      setFilteredProjects(projects.slice(0, 10));
    } else {
      setFilteredProjects(projects.filter(p => p.status === statusFilter).slice(0, 10));
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'do_realizacji': { label: 'Do Realizacji', color: 'bg-[#FF6B35] text-white' },
      'w_toku': { label: 'W Toku', color: 'bg-amber-500 text-[var(--crm-text-primary)]' },
      'zakończony': { label: 'Zakończony', color: 'bg-emerald-500 text-[var(--crm-text-primary)]' }
    };
    const variant = variants[status] || { label: status, color: 'bg-slate-500 text-[var(--crm-text-primary)]' };
    return <Badge className={variant.color}>{variant.label}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-[var(--crm-text-primary)]">Ładowanie...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8 lg:p-12">
        {/* Mobile KPI Widget - only visible on mobile for non-montaz/guest roles */}
        {user?.role !== 'montaz' && user?.role !== 'guest' && (
          <div className="md:hidden mb-4">
            <KPISidebar />
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-4xl font-manrope font-bold text-[var(--crm-text-primary)] tracking-tight">Strona główna</h1>
            <p className="text-[var(--crm-text-secondary)] mt-1 text-sm md:text-base">Przegląd zleceń</p>
          </div>
          {user?.role !== 'guest' && user?.role !== 'montaz' && (
            <Link to="/projects/new">
              <Button data-testid="add-project-button" className="w-full sm:w-auto bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white">
                <Plus className="w-4 h-4 mr-2" />
                Nowe Zlecenie
              </Button>
            </Link>
          )}
        </div>

        {/* Stale Projects Banner */}
        <StaleProjectsBanner />

        {/* Mobile Quick Actions - only on small screens, for non-restricted roles */}
        {user?.role !== 'montaz' && user?.role !== 'guest' && (
          <div className="md:hidden mb-4" data-testid="mobile-quick-actions">
            <h2 className="text-sm font-semibold text-[var(--crm-text-secondary)] uppercase tracking-wider mb-3">Szybkie akcje</h2>
            {/* Row 1 — 2 large cards */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <Link to="/lead-terenowy" data-testid="quick-action-new-lead">
                <div className="bg-gradient-to-br from-[#FF6B35] to-[#F59E0B] rounded-xl p-4 flex flex-col items-center justify-center gap-2 active:scale-95 transition-transform min-h-[96px] shadow-lg">
                  <MapPin className="w-7 h-7 text-[var(--crm-text-primary)]" />
                  <span className="text-sm font-bold text-[var(--crm-text-primary)] text-center leading-tight">Nowy Lead z trasy</span>
                </div>
              </Link>
              <Link to="/projects/new" data-testid="quick-action-new-project">
                <div className="bg-gradient-to-br from-[#3B82F6] to-[#6366F1] rounded-xl p-4 flex flex-col items-center justify-center gap-2 active:scale-95 transition-transform min-h-[96px] shadow-lg">
                  <FilePlus className="w-7 h-7 text-[var(--crm-text-primary)]" />
                  <span className="text-sm font-bold text-[var(--crm-text-primary)] text-center leading-tight">Nowe Zlecenie</span>
                </div>
              </Link>
            </div>
            {/* Row 2 — 2 smaller cards */}
            <div className="grid grid-cols-1 gap-3">
              <Link to="/klienci" data-testid="quick-action-clients">
                <div className="bg-[var(--crm-bg-dialog)] border border-[var(--crm-border)] rounded-xl p-3 flex items-center gap-3 active:scale-95 transition-transform min-h-[56px]">
                  <UsersIcon className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <div>
                    <span className="text-xs font-semibold text-[var(--crm-text-primary)] block leading-tight">Moi klienci</span>
                    <span className="text-[10px] text-[var(--crm-text-secondary)]">{stats?.total_projects || 0} zleceń</span>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        )}

        {/* Financial Stats with Time Toggle */}
        {user?.role !== 'montaz' && user?.role !== 'guest' && (
          <div className="mb-6" data-testid="financial-stats-section">
            {/* Time Toggle */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-manrope font-bold text-[var(--crm-text-primary)] flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#FF6B35]" />
                Finanse
                <span className="text-xs font-normal text-[var(--crm-text-secondary)] bg-[var(--crm-bg-panel)] px-2 py-0.5 rounded ml-1" data-testid="scope-label">
                  {user?.role === 'handlowiec_b2b' ? 'Twoje statystyki' : 'Statystyki globalne firmy'}
                </span>
              </h2>
              <div className="flex bg-[var(--crm-bg-panel)] rounded-lg border border-[var(--crm-border)] p-0.5" data-testid="time-filter-toggle">
                {TIME_FILTERS.map(tf => (
                  <button key={tf.value} onClick={() => setTimeFilter(tf.value)}
                    data-testid={`time-filter-${tf.value}`}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      timeFilter === tf.value
                        ? 'bg-[#FF6B35] text-white shadow-sm'
                        : 'text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)]'
                    }`}>
                    {tf.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 3 Financial Tiles + Margin */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              <Card data-testid="stat-accepted" style={{boxShadow:"var(--crm-card-shadow)"}} className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4 md:p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center">
                    <Package className="w-5 h-5 text-amber-500" />
                  </div>
                  <span className="text-xs font-medium text-[var(--crm-text-secondary)]">Zaakceptowane</span>
                </div>
                <p className="text-xl md:text-2xl font-manrope font-bold text-[var(--crm-text-primary)]">
                  {dashStats ? `${(dashStats.val_accepted / 1000).toFixed(0)}k` : '—'} <span className="text-sm text-[var(--crm-text-secondary)] font-normal">zł</span>
                </p>
                <p className="text-xs text-[var(--crm-text-muted)] mt-1">{dashStats?.count_accepted || 0} zleceń</p>
              </Card>

              <Card data-testid="stat-in-progress" style={{boxShadow:"var(--crm-card-shadow)"}} className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4 md:p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                    <Truck className="w-5 h-5 text-blue-500" />
                  </div>
                  <span className="text-xs font-medium text-[var(--crm-text-secondary)]">W realizacji</span>
                </div>
                <p className="text-xl md:text-2xl font-manrope font-bold text-[var(--crm-text-primary)]">
                  {dashStats ? `${(dashStats.val_in_progress / 1000).toFixed(0)}k` : '—'} <span className="text-sm text-[var(--crm-text-secondary)] font-normal">zł</span>
                </p>
                <p className="text-xs text-[var(--crm-text-muted)] mt-1">{dashStats?.count_in_progress || 0} zleceń</p>
              </Card>

              <Card data-testid="stat-completed-value" style={{boxShadow:"var(--crm-card-shadow)"}} className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4 md:p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                    <CircleCheckBig className="w-5 h-5 text-emerald-500" />
                  </div>
                  <span className="text-xs font-medium text-[var(--crm-text-secondary)]">Zakończone</span>
                </div>
                <p className="text-xl md:text-2xl font-manrope font-bold text-[var(--crm-text-primary)]">
                  {dashStats ? `${(dashStats.val_completed / 1000).toFixed(0)}k` : '—'} <span className="text-sm text-[var(--crm-text-secondary)] font-normal">zł</span>
                </p>
                <p className="text-xs text-[var(--crm-text-muted)] mt-1">{dashStats?.count_completed || 0} zleceń</p>
              </Card>

              <Card data-testid="stat-avg-margin" style={{boxShadow:"var(--crm-card-shadow)"}} className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4 md:p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-[#FF6B35]" />
                  </div>
                  <span className="text-xs font-medium text-[var(--crm-text-secondary)] flex items-center gap-1">
                    Śr. Marża <HelpTooltip text="Średnia marża % z aktywnych i zakończonych zleceń w wybranym okresie" />
                  </span>
                </div>
                <p className="text-xl md:text-2xl font-manrope font-bold text-[var(--crm-text-primary)]">
                  {dashStats ? `${dashStats.avg_margin_percent}%` : '—'}
                </p>
                <p className="text-xs text-[var(--crm-text-muted)] mt-1">{dashStats?.total_active || 0} aktywnych</p>
              </Card>
            </div>
          </div>
        )}

        {/* Summary row — project count */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
          <Card data-testid="stat-total-projects" style={{boxShadow:"var(--crm-card-shadow)"}} className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4 md:p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center">
                <FolderKanban className="w-5 h-5 text-[#FF6B35]" />
              </div>
              <span className="text-xs font-medium text-[var(--crm-text-secondary)]">Aktywne zlecenia</span>
            </div>
            <p className="text-2xl md:text-3xl font-manrope font-bold text-[var(--crm-text-primary)]">{stats?.total_projects || 0}</p>
          </Card>
        </div>

        {/* Pipeline Value Section */}
        {pipeline && user?.role !== 'montaz' && user?.role !== 'guest' && (
          <div className="mb-6" data-testid="pipeline-section">
            <h2 className="text-lg md:text-xl font-manrope font-bold text-[var(--crm-text-primary)] mb-3 flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#FF6B35]" />
              Wartość pipeline
              <HelpTooltip text="Suma wartości wszystkich aktywnych projektów w toku (bez zakończonych i odrzuconych)" />
              <span className="text-sm font-normal text-[var(--crm-text-secondary)] ml-2">
                {pipeline.total_pipeline.toLocaleString('pl-PL')} zł ({pipeline.active_count} aktywnych)
              </span>
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Status breakdown cards */}
              <div className="lg:col-span-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-1 gap-2">
                {pipeline.by_status.filter(s => s.count > 0 || s.value > 0).length > 0
                  ? pipeline.by_status.map(s => (
                    <Card key={s.key} className={`bg-[var(--crm-bg-panel)] border-[var(--crm-border)] p-3 ${s.count === 0 ? 'opacity-40' : ''}`}
                      data-testid={`pipeline-status-${s.key}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[var(--crm-text-secondary)] text-[10px] uppercase tracking-wider">{s.label}</p>
                          <p className="text-[var(--crm-text-primary)] font-bold text-lg font-mono">
                            {s.value >= 1000 ? `${(s.value / 1000).toFixed(0)}k` : s.value.toLocaleString('pl-PL')} zł
                          </p>
                        </div>
                        <Badge className="bg-[var(--crm-bg-btn-secondary)] text-[var(--crm-text-primary)] text-xs">{s.count}</Badge>
                      </div>
                    </Card>
                  ))
                  : <p className="text-[var(--crm-text-muted)] text-sm col-span-full text-center py-4">Brak aktywnych projektów</p>
                }
              </div>

              {/* Trend chart */}
              <Card className="lg:col-span-2 bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4" data-testid="pipeline-trend-chart">
                <p className="text-[var(--crm-text-secondary)] text-xs mb-3">Wartość pipeline — ostatnie 6 miesięcy</p>
                <div className="h-[180px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={pipeline.trend.map(t => ({
                      name: t.month.slice(5) + '/' + t.month.slice(2, 4),
                      value: t.value
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334E78" />
                      <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#334E78' }} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#334E78' }}
                        tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1a2d4d', border: '1px solid #334E78', borderRadius: '8px' }}
                        labelStyle={{ color: '#94a3b8' }}
                        formatter={(value) => [`${value.toLocaleString('pl-PL')} zł`, 'Pipeline']}
                      />
                      <Line type="monotone" dataKey="value" stroke="#FF6B35" strokeWidth={2.5}
                        dot={{ fill: '#FF6B35', r: 4 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* Projects List */}
        <div className="bg-[var(--crm-bg-dialog)] border border-[var(--crm-border)] rounded-lg p-4 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <h2 className="text-lg md:text-2xl font-manrope font-bold text-[var(--crm-text-primary)]">Ostatnie zlecenia</h2>
            <div className="flex items-center gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="dashboard-filter-status" className="w-full sm:w-40 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] text-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                  <SelectItem value="all">Wszystkie</SelectItem>
                  <SelectItem value="do_realizacji">Do Realizacji</SelectItem>
                  <SelectItem value="w_toku">W Toku</SelectItem>
                  <SelectItem value="zakończony">Zakończony</SelectItem>
                </SelectContent>
              </Select>
              <Link to="/projects" className="hidden sm:block">
                <Button size="sm" className="bg-[var(--crm-bg-btn-secondary)] hover:bg-[var(--crm-bg-card-hover)] text-[var(--crm-text-primary)]">
                  Wszystkie
                </Button>
              </Link>
            </div>
          </div>

          {/* Project Cards */}
          <div className="space-y-2">
            {filteredProjects.length === 0 ? (
              <div className="text-center text-[var(--crm-text-secondary)] py-8">
                Brak zleceń do wyświetlenia
              </div>
            ) : (
              filteredProjects.map((project) => (
                <div
                  key={project.id}
                  onClick={() => navigate(`/projects/${project.id}`)}
                  data-testid={`dashboard-project-${project.id}`}
                  className="flex items-center justify-between p-3 md:p-4 bg-[var(--crm-bg-panel)] rounded-lg cursor-pointer hover:bg-[var(--crm-bg-card-hover)] active:bg-white/10 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-[var(--crm-text-primary)] font-medium truncate">{project.nazwa}</h3>
                      {getStatusBadge(project.status)}
                    </div>
                    <p className="text-[var(--crm-text-secondary)] text-sm truncate">{project.klient}</p>
                  </div>
                  <div className="flex items-center gap-3 ml-3">
                    {user?.role !== 'montaz' && (
                      <div className="text-right hidden sm:block">
                        <p className="font-mono font-bold text-emerald-400">
                          {project.total_offer.toLocaleString('pl-PL')} zł
                        </p>
                        <p className={`text-xs font-medium ${(project.net_margin_percent ?? project.margin_percent) >= 0 ? 'text-amber-400' : 'text-red-400'}`}>
                          Marża netto: {(project.net_margin_percent ?? project.margin_percent)?.toFixed(1)}%
                        </p>
                      </div>
                    )}
                    <ChevronRight className="w-5 h-5 text-[var(--crm-text-secondary)]" />
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Mobile "See all" button */}
          <Link to="/projects" className="sm:hidden block mt-4">
            <Button className="w-full bg-[var(--crm-bg-btn-secondary)] hover:bg-[var(--crm-bg-card-hover)] text-[var(--crm-text-primary)]">
              Zobacz wszystkie zlecenia
            </Button>
          </Link>
        </div>
      </div>
    </Layout>
  );
};
